export const MAX_INPUT_SIZE_MB = 7

const COMPRESSION = {
  maxWidth: 2400,
  maxHeight: 1800,
  quality: 0.95,
  maxSizeBytes: 2.8 * 1024 * 1024,
} as const

const OUTPUT_TYPE = "image/jpeg"

const replaceExtension = (filename: string): string =>
  filename.replace(/\.[^.]+$/, ".jpg")

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("Failed to load image"))
    img.src = src
  })

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Failed to compress image")),
      type,
      quality
    )
  )

export const compressImage = async (file: File): Promise<File> => {
  if (file.size <= COMPRESSION.maxSizeBytes) {
    return file
  }

  const objectUrl = URL.createObjectURL(file)
  try {
    const img = await loadImage(objectUrl)

    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    if (!ctx) {
      return file
    }

    let { width, height } = img
    const aspectRatio = width / height

    if (width > COMPRESSION.maxWidth) {
      width = COMPRESSION.maxWidth
      height = width / aspectRatio
    }

    if (height > COMPRESSION.maxHeight) {
      height = COMPRESSION.maxHeight
      width = height * aspectRatio
    }

    canvas.width = width
    canvas.height = height

    // Fill white background to prevent PNG transparency from appearing black in JPEG output
    if (file.type !== "image/jpeg") {
      ctx.fillStyle = "#FFFFFF"
      ctx.fillRect(0, 0, width, height)
    }

    ctx.drawImage(img, 0, 0, width, height)

    let quality: number = COMPRESSION.quality
    let blob = await canvasToBlob(canvas, OUTPUT_TYPE, quality)

    while (blob.size > COMPRESSION.maxSizeBytes && quality > 0.3) {
      quality = Math.max(0.3, quality - 0.1)
      blob = await canvasToBlob(canvas, OUTPUT_TYPE, quality)
    }

    return new File([blob], replaceExtension(file.name), {
      type: OUTPUT_TYPE,
      lastModified: Date.now(),
    })
  } catch {
    return file
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
